import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Paper, Stack, TextField, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HowToRegTwoTone from "@mui/icons-material/HowToRegTwoTone";
import { toast } from "react-toastify";
import LocalstorageHelper from "../../LocalStorageHelper/LocalstorageHelper";

toast.configure();

const columns = [
  { field: "inwardno", headerName: "Inward No", minWidth: 150, flex: 0.8 },
  { field: "billno", headerName: "Bill No", minWidth: 160, flex: 0.9 },
  { field: "inwarddate", headerName: "Inward Date", minWidth: 130, flex: 0.7 },
  { field: "branchname", headerName: "Branch", minWidth: 160, flex: 0.8 },
  { field: "subledgername", headerName: "Party", minWidth: 260, flex: 1.4 },
  {
    field: "billamount",
    headerName: "Bill Amount",
    type: "number",
    minWidth: 130,
    flex: 0.7,
    valueFormatter: ({ value }) =>
      Number(value || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  },
];

async function readResponseMessage(response) {
  const text = await response.text();
  if (!text) return response.statusText || "Server Error";

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === "string") return parsed;
    return parsed.message || JSON.stringify(parsed);
  } catch (err) {
    return text;
  }
}

const PurchaseApproveWorkspace = ({ token }) => {
  const userToken = token || LocalstorageHelper.GetUserToken();
  const [rows, setRows] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [fromdate, setFromdate] = useState("");
  const [todate, setTodate] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const fetchPurchaseBills = useCallback(async (filters = {}) => {
    if (!userToken) return;

    const activeFromdate = filters.fromdate || "";
    const activeTodate = filters.todate || "";
    const activeSearch = filters.search || "";

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFromdate) params.set("fromdate", activeFromdate);
      if (activeTodate) params.set("todate", activeTodate);
      if (activeSearch.trim()) params.set("search", activeSearch.trim());

      const response = await fetch(`/purbilldtl?${params.toString()}`, {
        method: "GET",
        headers: { sptoken: userToken },
      });

      if (!response.ok) {
        throw new Error(await readResponseMessage(response));
      }

      const data = await response.json();
      setRows(Array.isArray(data) ? data : []);
      setSelectionModel([]);
    } catch (err) {
      toast.error(err.message || "Could not fetch pending purchase bills.");
    } finally {
      setLoading(false);
    }
  }, [userToken]);

  useEffect(() => {
    if (userToken) {
      fetchPurchaseBills({ fromdate: "", todate: "", search: "" });
    }
  }, [fetchPurchaseBills, userToken]);

  const handleReset = () => {
    setFromdate("");
    setTodate("");
    setSearch("");
    fetchPurchaseBills({ fromdate: "", todate: "", search: "" });
  };

  const handleApproveSelected = async () => {
    if (selectionModel.length === 0) {
      toast.warn("Select at least one purchase bill.");
      return;
    }

    setApproving(true);
    try {
      const response = await fetch("/apprvpurbill/bulk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          sptoken: userToken,
        },
        body: JSON.stringify({ inwardids: selectionModel }),
      });

      if (!response.ok) {
        throw new Error(await readResponseMessage(response));
      }

      const result = await response.json();
      const approvedIds = new Set(
        (result.approvedBills || []).map((bill) => Number(bill.inwardid)),
      );
      const approvedCount = Number(result.approvedCount || selectionModel.length);

      setRows((currentRows) =>
        currentRows.filter((row) => !approvedIds.has(Number(row.inwardid))),
      );
      setSelectionModel([]);
      toast.success(`${approvedCount} purchase bill${approvedCount > 1 ? "s" : ""} approved.`);
    } catch (err) {
      toast.error(err.message || "Purchase approval failed.");
    } finally {
      setApproving(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <TextField
            label="From Date"
            type="date"
            size="small"
            value={fromdate}
            onChange={(e) => setFromdate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="date"
            size="small"
            value={todate}
            onChange={(e) => setTodate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Bill / Inward / Party"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                fetchPurchaseBills({ fromdate, todate, search });
              }
            }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={() => fetchPurchaseBills({ fromdate, todate, search })}
            disabled={loading || approving}
          >
            Search
          </Button>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleReset}
            disabled={loading || approving}
          >
            Reset
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Typography fontWeight="bold">
            Selected: {selectionModel.length}
          </Typography>
          <Button
            variant="contained"
            color="success"
            startIcon={<HowToRegTwoTone />}
            onClick={handleApproveSelected}
            disabled={selectionModel.length === 0 || loading || approving}
          >
            {approving ? "Approving..." : "Approve Selected"}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ height: "68vh", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.inwardid}
          checkboxSelection
          disableSelectionOnClick
          rowHeight={32}
          headerHeight={42}
          loading={loading}
          selectionModel={selectionModel}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          pageSize={100}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>
    </Box>
  );
};

export default PurchaseApproveWorkspace;
